import { Link } from "react-router-dom";
import { HERO_IMG, MOOD_IMG } from "@/lib/api";
import Slideshow from "@/components/Slideshow";
import {
  FolderTree, FileSearch2, GitBranch, BarChart3,
  MailWarning, Cloud, ShieldCheck, ArrowRight, Sparkles, Cpu, MonitorPlay,
} from "lucide-react";

const features = [
  { icon: FolderTree, title: "Smart folder sorting", desc: "Train a category with 2-3 sample files. The engine learns the language of your documents and routes thousands automatically.", span: "md:col-span-7 md:row-span-2" },
  { icon: FileSearch2, title: "Duplicate detection", desc: "SHA-256 fingerprints every file. Same content = caught, no matter the filename.", span: "md:col-span-5" },
  { icon: GitBranch, title: "Version history", desc: "Same name, different content? Keeps both with -v2, -v3 suffixes. Never overwrites.", span: "md:col-span-5" },
  { icon: BarChart3, title: "Reports + Excel export", desc: "8 built-in reports with filters and one-click Excel export, embed charts.", span: "md:col-span-6" },
  { icon: MailWarning, title: "Email reports + alerts", desc: "Wire any SMTP. Daily/weekly reports + real-time failure alerts.", span: "md:col-span-6" },
  { icon: Cloud, title: "Cloud destinations", desc: "Optionally upload to AWS S3, Azure Blob or Google Cloud. Local copy always first.", span: "md:col-span-6" },
  { icon: ShieldCheck, title: "Resilient by design", desc: "Transaction journal, verify-after-write, auto-pause on drop, 10-min retry.", span: "md:col-span-6" },
];

const steps = [
  ["01", "Download & run", "One zip. Unzip. Double-click TechnoSort.exe."],
  ["02", "Create admin", "First-run setup makes one Administrator account."],
  ["03", "Point at a folder", "Pick your scanner output folder. Toggle Keep Original."],
  ["04", "Define categories", "One bucket per document type. Train with 2-3 samples."],
  ["05", "Run Now", "Click the blue button. Watch the dashboard. Done."],
];

const INDUSTRIES = [
  "Oil Refinery", "Gas Plant", "Legal Office", "Construction Firms",
  "Pharma Company", "Healthcare", "Banking Sector", "Accountants",
  "Lawyers", "Clinics", "Schools", "Scanning Bureaus", "CA Firms",
  "Government", "Mid-level Business",
];

const DASHBOARD_IMG = "https://customer-assets.emergentagent.com/job_elite-payment-hub/artifacts/eva3b8bx_use%20thsi.png";
const SETTINGS_IMG  = "https://customer-assets.emergentagent.com/job_elite-payment-hub/artifacts/aee8q921_screesnhsot%202.png";

// Product screenshot slideshow — original cockpit/dashboard kept as the default
// first slide; the 4 new user-supplied screenshots cycle after it.
const PRODUCT_SLIDES = [
  {
    src: DASHBOARD_IMG,
    alt: "Techno Sort — Cockpit / Dashboard",
    caption: "Cockpit · Dashboard",
    theme: "dark",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_can-you-load-1/artifacts/mqnias8k_white%20theme.png",
    alt: "Techno Sort — Settings · Advanced (white theme)",
    caption: "Settings · Advanced — White theme",
    theme: "light",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_can-you-load-1/artifacts/8flje6b0_ChatGPT%20Image%20Jun%2023%2C%202026%2C%2005_58_01%20PM.png",
    alt: "Techno Sort — Settings · Advanced",
    caption: "Settings · Advanced",
    theme: "dark",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_can-you-load-1/artifacts/6q3bxsbw_ChatGPT%20Image%20Jun%2023%2C%202026%2C%2006_04_34%20PM.png",
    alt: "Techno Sort — Documents by Category",
    caption: "Documents by Category",
    theme: "dark",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_can-you-load-1/artifacts/h3qn08k9_ChatGPT%20Image%20Jun%2023%2C%202026%2C%2006_08_57%20PM.png",
    alt: "Techno Sort — Folder Category Management",
    caption: "Folder Category Management",
    theme: "dark",
  },
];

export default function Home() {
  return (
    <>
      {/* HERO */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 -z-0">
          <img src={HERO_IMG} className="w-full h-full object-cover opacity-40" alt="" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/85 to-black" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(244,162,42,0.18),transparent_40%),radial-gradient(circle_at_80%_60%,rgba(30,91,168,0.15),transparent_40%)]" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 pt-24 pb-24">
          <div className="reveal-up inline-flex items-center gap-2 text-[11px] tracking-[0.25em] uppercase text-neutral-300 border border-white/15 px-3 py-1.5 bg-white/5 backdrop-blur-sm">
            <Sparkles className="w-3 h-3 text-[#F4A22A]" />
            Powered by AI
          </div>

          <h1 className="reveal-up mt-8 font-display font-bold text-white text-5xl md:text-7xl lg:text-8xl leading-[0.95]" style={{ animationDelay: "120ms" }}>
            Stop Dragging
            <br />
            <span className="text-[#F4A22A]">Files Into Folders.</span>
          </h1>

          <p className="reveal-up mt-6 text-[11px] tracking-[0.3em] uppercase text-[#F4A22A] font-medium" style={{ animationDelay: "180ms" }}>
            Intelligent Document Automation
          </p>

          <p className="reveal-up max-w-2xl mt-6 text-neutral-300 text-lg leading-relaxed" style={{ animationDelay: "240ms" }}>
            Techno Sort reads every PDF, image and Office document in your input folder,
            figures out what it is, and moves it to the right place — automatically.
            Works completely offline. One-time purchase.
          </p>

          <div className="reveal-up mt-8 border-l-2 border-[#F4A22A] pl-5 max-w-2xl" style={{ animationDelay: "300ms" }} data-testid="hero-anti-subscription">
            <div className="font-display font-bold text-xl md:text-2xl text-white leading-snug">
              Your files don't need a subscription.
            </div>
            <p className="text-neutral-400 text-sm mt-2 leading-relaxed">
              Techno Sort classifies and routes every PDF, DOCX, XLSX, and image on your machine —
              <span className="text-white"> offline, on-prem, one-time license</span>.
            </p>
          </div>

          <div className="reveal-up mt-10 flex flex-wrap items-center gap-4" style={{ animationDelay: "360ms" }}>
            <Link to="/pricing" className="btn-primary px-7 py-4 text-sm font-bold uppercase tracking-wider inline-flex items-center gap-2" data-testid="hero-cta-pricing">
              See Pricing <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/download" className="border border-white/25 text-white hover:bg-white/5 px-7 py-4 text-sm font-bold uppercase tracking-wider transition-colors" data-testid="hero-cta-download">
              Download Free
            </Link>
          </div>

          <div className="reveal-up mt-12 text-[11px] uppercase tracking-[0.25em] text-neutral-500 flex flex-wrap items-center gap-3" style={{ animationDelay: "480ms" }}>
            <span className="px-2 py-1 border border-white/10 text-neutral-300">Windows 10</span>
            <span className="px-2 py-1 border border-white/10 text-neutral-300">Windows 11</span>
            <span className="px-2 py-1 border border-white/10 text-neutral-300">Server 2019 → 2025</span>
            <span className="text-neutral-500">· Offline · One-time purchase</span>
          </div>
        </div>

        {/* Industry marquee */}
        <div className="relative border-y border-white/10 bg-black/60 py-5 overflow-hidden">
          <div className="marquee whitespace-nowrap flex gap-16 text-neutral-400 text-sm uppercase tracking-[0.25em]">
            {[...INDUSTRIES, ...INDUSTRIES].map((w, i) => (
              <span key={i} className="inline-flex items-center gap-3">
                <span className="w-1 h-1 rounded-full bg-[#F4A22A]" />{w}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* APP PREVIEW (real dashboard + settings) */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-28" data-testid="preview-section">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
          <div>
            <div className="text-xs tracking-[0.25em] uppercase text-neutral-400 mb-3">Inside the app</div>
            <h2 className="font-display font-bold text-4xl md:text-5xl text-white">
              A live cockpit for your filing room.
            </h2>
            <p className="text-neutral-400 mt-4 max-w-xl">Dashboard, sources, destinations, cloud — everything you need on one screen.</p>
          </div>
          <a href="https://www.youtube.com/@Techno_Sort-RD" target="_blank" rel="noreferrer"
             className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#F4A22A] hover:text-white transition-colors"
             data-testid="preview-youtube-link">
            <MonitorPlay className="w-4 h-4" /> Watch tutorials on YouTube
          </a>
        </div>

        <Slideshow slides={PRODUCT_SLIDES} intervalMs={5000} />
      </section>

      {/* FEATURES bento */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-28" data-testid="features-section">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-14">
          <div>
            <div className="text-xs tracking-[0.25em] uppercase text-neutral-400 mb-3">Features</div>
            <h2 className="font-display font-bold text-4xl md:text-5xl text-white">
              Everything a document-heavy team needs.
            </h2>
            <p className="text-neutral-400 mt-4 max-w-xl">No subscriptions. No cloud. No surprises.</p>
          </div>
          <div className="flex items-center gap-2 text-xs tracking-[0.25em] uppercase text-neutral-500">
            <Cpu className="w-4 h-4 text-[#1E5BA8]" /> AI-Native Engine
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-5 auto-rows-[180px]">
          {features.map((f, i) => {
            const Icon = f.icon;
            return (
              <div key={f.title} className={`card-tile p-7 ${f.span || "md:col-span-4"} flex flex-col justify-between`} data-testid={`feature-card-${i}`}>
                <Icon className="w-7 h-7 text-[#F4A22A]" />
                <div>
                  <div className="font-display font-bold text-xl text-white tracking-tight">{f.title}</div>
                  <p className="text-neutral-400 text-sm mt-2 leading-relaxed">{f.desc}</p>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* STEPS */}
      <section className="relative" data-testid="steps-section">
        <div className="absolute inset-0 -z-0">
          <img src={MOOD_IMG} className="w-full h-full object-cover opacity-25" alt="" />
          <div className="absolute inset-0 bg-gradient-to-b from-black via-black/85 to-black" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-28">
          <div className="text-xs tracking-[0.25em] uppercase text-neutral-400 mb-3">Workflow</div>
          <h2 className="font-display font-bold text-4xl md:text-5xl text-white max-w-3xl">
            Five clicks from install to sorted.
          </h2>

          <div className="grid md:grid-cols-5 gap-px bg-white/10 mt-14 border border-white/10">
            {steps.map(([num, title, desc]) => (
              <div key={num} className="bg-black p-7 min-h-[200px] flex flex-col justify-between">
                <div className="font-mono text-[#F4A22A] text-sm">{num}</div>
                <div>
                  <div className="font-display font-bold text-lg text-white">{title}</div>
                  <p className="text-neutral-400 text-sm mt-2 leading-relaxed">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-28">
        <div className="card-tile p-12 md:p-16 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <div>
            <h3 className="font-display font-bold text-3xl md:text-4xl text-white max-w-xl">
              Ready to retire the drag-and-drop?
            </h3>
            <p className="text-neutral-400 mt-3 max-w-xl">Pick a tier, pay via Razorpay (USD), receive your license + download in 60 seconds.</p>
          </div>
          <div className="flex gap-3">
            <Link to="/pricing" className="btn-primary px-7 py-4 text-sm font-bold uppercase tracking-wider" data-testid="cta-pricing-button">
              View Plans
            </Link>
            <Link to="/contact" className="border border-white/25 hover:bg-white/5 text-white px-7 py-4 text-sm font-bold uppercase tracking-wider transition-colors" data-testid="cta-contact-button">
              Talk to Sales
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
